import styled from "@emotion/styled";
import { Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { arrType, datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import axios from "axios";
import { mearchatSkuDummyData } from "../../MerchantData/data";
import { debounce } from "lodash";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	shootMerchantSampleDetailsColumns,
	shootSkuActionDgCol,
	shootSkuIcodeColumn,
	shootSkuItemMasterColumns,
	shootStudioSampleDetailsColumns,
} from "components/PimDashboard/DashboardComponents/Studio/ShootsDashboard/data";

export const TableImgComponent = ({ data, column }) => (
	<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
		<Box
			component="label"
			width="45px"
			height="55px"
			border={`1px solid ${grey[200]}`}
			display="flex"
			alignItems="center"
			justifyContent="center"
			overflow="hidden"
			position="relative">
			<img src={data?.[column]} style={{ width: "100%", position: "absolute" }} />
		</Box>
	</Box>
);

export const skuSerchKeyCol = {
	name: "SKU",
	key: "sku",
	input_type: inputTypes.MULTIPLE_SELECT.key,
	constraints: { NOTNULL: false, DISPLAY: true, UNIQUE: false },
	data: {},
	data_type: datatypes.STRING.key,
	sort_no: 1,
	group: "",
	placeholder: "",
	default_value: "",
	[selectOptionsKeyName]: {
		type: arrType,
		list: [
			{ name: "MAINLINE", key: "MAINLINE" },
			{ name: "GUILD", key: "GUILD" },
		],
		getOptionLabel: (opt) => ({ name: opt.modal_profile_name, key: opt.modal_profile_name }),
	},
};

const SkuSection = ({
	shootData,
	selectedSkus, // array of strings (i_code)
	setSelectedSkus, // setter for array of strings
	minHeight = 300,
	maxHeight = 300,
	onRowClick = (row, event) => {},
	skusState, // optional
	setSkusState, // optional
	hideSearchHeader = false,
	showActionColumn = false,
	actionList = [],
	disableSelection = false,
}) => {
	const { setSnackBar, setSnackBarOpen, snackBar } = GlobalState();

	const [tableColumns] = useState([
		shootSkuIcodeColumn,
		...Object.values(shootSkuItemMasterColumns),
		...Object.values(shootStudioSampleDetailsColumns),
		...Object.values(shootMerchantSampleDetailsColumns),
	]);

	const [suggestions, setSuggestionsInternal] = useState([]);
	// const [defaultLoad, setDefaultLoad] = useState([mearchatSkuDummyData]);
	const [defaultLoad, setDefaultLoad] = useState([]);
	const [loading, setLoading] = useState(false);
	const [query, setQuery] = useState("");

	// Internal map to store full row objects keyed by i_code
	const [selectedRowsMap, setSelectedRowsMap] = useState(new Map());

	const finalSuggestions = skusState ?? suggestions;
	const setFinalSuggestions = setSkusState ?? setSuggestionsInternal;

	const getSkus = async (offset = 0, recordsLimit = 20, searchTerm = "") => {
		const queryObj = { limit: recordsLimit, offset };
		if (searchTerm) queryObj["search"] = searchTerm;
		const res = await axios.get(`/api/studio/skus_combined_data`, { params: queryObj });
		return res.data.result;
	};

	const fetchDefaultData = async () => {
		setLoading(true);
		try {
			const res = (await getSkus(0, 20)) || [];
			setDefaultLoad(res);
			setFinalSuggestions(res);
		} catch (err) {
			setSnackBar({ ...snackBar, message: parseErrorMessage(err), severity: "error" });
			setSnackBarOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const fetchSuggestions = async (searchTerm) => {
		if (!searchTerm.trim()) return;
		setLoading(true);
		try {
			const res = (await getSkus(0, 20, searchTerm)) || [];
			setFinalSuggestions(res);
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchDefaultData();
	}, []);

	const debouncedFetch = useMemo(() => debounce(fetchSuggestions, 1000), []);

	useEffect(() => {
		if (!query) {
			setFinalSuggestions(defaultLoad);
		} else {
			debouncedFetch(query);
		}
	}, [query]);

	useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

	// Compute rows to display in DataGrid
	const displayedRows = useMemo(() => {
		if (!selectedSkus?.length) return finalSuggestions;

		const selectedIds = new Set(selectedSkus);
		// get full row objects from map or finalSuggestions
		const selectedFullRows = selectedSkus
			.map((id) => selectedRowsMap.get(id) ?? finalSuggestions.find((r) => r.i_code === id))
			.filter(Boolean);

		// Append selected rows not in suggestions at bottom
		const selectedNotInSuggestions = selectedFullRows.filter((r) => !finalSuggestions.find((s) => s.i_code === r.i_code));

		return [...finalSuggestions, ...selectedNotInSuggestions];
	}, [finalSuggestions, selectedSkus, selectedRowsMap]);

	return (
		<Box display="flex" flexWrap="wrap" width="100%" rowGap={1}>
			<Typography variant="h6" width="100%" display={hideSearchHeader ? "none" : "block"}>
				Select SKU
			</Typography>
			<Box width="300px">
				<TextField
					fullWidth
					variant="outlined"
					size="small"
					label="Search Style/Season/Icode"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">{loading ? <CircularProgress size={20} /> : null}</InputAdornment>
						),
					}}
				/>
			</Box>
			<Box width="100%">
				{tableColumns?.length >= 0 && (
					<DataGrid
						rows={displayedRows || []}
						getRowId={(row) => row.i_code}
						columns={[
							...(showActionColumn ? [shootSkuActionDgCol(actionList)] : []),
							...tableColumns.map((colorCol) => ({
								field: colorCol?.key,
								headerName: colorCol?.name?.toUpperCase(),
								minWidth: 130,
								headerAlign: "center",
								align: "center",
								flex: 1,
							})),
						]}
						sx={{
							minHeight,
							...(maxHeight ? { maxHeight } : {}),
							"& .MuiDataGrid-row": { borderBottom: "1px solid lightgray", cursor: "pointer" },
							"& .MuiDataGrid-columnHeader": { borderRight: "1px solid lightgray" },
						}}
						columnHeaderHeight={35}
						rowHeight={40}
						pageSizeOptions={[5, 10]}
						checkboxSelection={disableSelection ? false : true}
						disableAutosize
						loading={loading}
						onRowClick={({ row }, event) => onRowClick(row, event)}
						rowSelectionModel={selectedSkus}
						onRowSelectionModelChange={(newIds) => {
							// Get full row objects for map
							const newSelectedFullRows = newIds.map((id) => displayedRows.find((r) => r.i_code === id)).filter(Boolean);

							// Update internal map
							const newMap = new Map(selectedRowsMap);
							newSelectedFullRows.forEach((r) => newMap.set(r.i_code, r));
							setSelectedRowsMap(newMap);

							// Update parent only with i_code strings
							setSelectedSkus(newIds);
						}}
					/>
				)}
			</Box>
		</Box>
	);
};

export default SkuSection;
